import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Paper,
  Stack,
  Title,
  Text,
  Button,
  Select,
  Alert,
  Loader,
  Group,
  Box,
} from '@vipso/shared';
import { setAuthFromInit } from '../store/authSlice';
import { mapInitAppPayload } from '../store/authUtils';
import type { RootState } from '../store';
import { buildInitPayload, DEMO_ORGS, getOrgTheme } from '../mock/mockData';

export function OrgPickerPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const orgs = useSelector((state: RootState) => state.auth.orgs);
  const tenantOrgs = useSelector((state: RootState) => state.auth.tenantOrgs);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);

  const autoOrgId = (location.state as { autoOrgId?: string } | null)?.autoOrgId;

  const options = useMemo(
    () =>
      orgs.length
        ? orgs.map((org) => ({ value: org.orgId, label: org.name || org.orgId }))
        : tenantOrgs.map((org) => ({ value: org.id, label: org.name || org.id })),
    [orgs, tenantOrgs],
  );

  const selectedTheme = selectedOrgId ? getOrgTheme(selectedOrgId) : null;

  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    if (autoOrgId) {
      setSelectedOrgId(autoOrgId);
    } else if (options.length === 1) {
      setSelectedOrgId(options[0].value);
    }
  }, [user, options, autoOrgId, navigate]);

  useEffect(() => {
    if (autoOrgId && user && !isLoading) {
      void handleContinue(autoOrgId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOrgId, user]);

  async function handleContinue(orgId?: string) {
    const targetOrgId = orgId || selectedOrgId;
    if (!user || !targetOrgId) return;

    setIsLoading(true);
    setError(false);
    try {
      const payload = buildInitPayload(targetOrgId, user.phone);
      dispatch(setAuthFromInit(mapInitAppPayload(payload, { user })));
      navigate('/dashboard', { replace: true });
    } catch {
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }

  if (!user) return null;

  if (autoOrgId && isLoading) {
    return (
      <Stack align="center" justify="center" style={{ minHeight: '100vh' }}>
        <Loader />
        <Text c="dimmed">{t('orgPicker.starting')}</Text>
      </Stack>
    );
  }

  return (
    <Stack align="center" justify="center" style={{ minHeight: '100vh' }} p="md">
      <Paper p="xl" withBorder w="100%" maw={480}>
        <Stack gap="md">
          <Stack gap={4}>
            <Title order={2}>{t('orgPicker.title')}</Title>
            <Text c="dimmed" size="sm">
              {t('orgPicker.subtitleDemo', { name: user.name })}
            </Text>
          </Stack>

          {error ? (
            <Alert color="red" title={t('orgPicker.initFailed')}>
              {t('orgPicker.initFailedHint')}
            </Alert>
          ) : null}

          {options.length === 0 ? (
            <Alert color="yellow" title={t('orgPicker.noOrgs')}>
              {t('orgPicker.noOrgsHint')}
            </Alert>
          ) : (
            <>
              <Select
                label={t('orgPicker.organization')}
                placeholder={t('orgPicker.selectPlaceholder')}
                value={selectedOrgId}
                onChange={(value) => setSelectedOrgId(value || '')}
                data={options}
                searchable
              />

              <Stack gap="xs">
                <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                  {t('orgPicker.brandThemes')}
                </Text>
                <Group gap="sm" wrap="wrap">
                  {DEMO_ORGS.map((org) => {
                    const active = selectedOrgId === org.orgId;
                    return (
                      <Box
                        key={org.orgId}
                        component="button"
                        type="button"
                        onClick={() => setSelectedOrgId(org.orgId)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '8px 12px',
                          borderRadius: 10,
                          border: active
                            ? `2px solid ${org.theme.primaryColor}`
                            : '1px solid var(--mantine-color-gray-3)',
                          background: active
                            ? org.theme.secondaryColor
                            : '#fff',
                          cursor: 'pointer',
                        }}
                      >
                        <span
                          style={{
                            width: 14,
                            height: 14,
                            borderRadius: '50%',
                            background: org.theme.primaryColor,
                            flexShrink: 0,
                          }}
                        />
                        <Text size="sm" fw={active ? 700 : 500}>
                          {org.name}
                        </Text>
                      </Box>
                    );
                  })}
                </Group>
                {selectedTheme ? (
                  <Text size="xs" c="dimmed">
                    {t('orgPicker.themePreview', {
                      primary: selectedTheme.primaryColor,
                      secondary: selectedTheme.secondaryColor,
                    })}
                  </Text>
                ) : null}
              </Stack>
            </>
          )}

          <Button
            loading={isLoading}
            disabled={!selectedOrgId}
            onClick={() => void handleContinue()}
            fullWidth
            style={
              selectedTheme
                ? { backgroundColor: selectedTheme.primaryColor }
                : undefined
            }
          >
            {t('common.continue')}
          </Button>
        </Stack>
      </Paper>
    </Stack>
  );
}
